import { addDebugLog } from "../utils/debugUtils";
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  X, Upload, FileText, CheckCircle, AlertCircle, 
  Building, User, Hash, Calendar, Euro, Percent, 
  TrendingUp, Clock, FileCheck, Terminal, Users,
  Lightbulb, Camera, Loader, Brain, Zap, Sparkles
, CreditCard, Home, Settings, Gavel, Megaphone, ClipboardCheck} from 'lucide-react';
import { masterSupabase as supabase } from '../lib/supabase-master.js';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';

// Supabase-conform cost categories
const costCategories = [
  { id: 'financing', name: 'Finanzierung', icon: CreditCard, color: 'blue', table: 'financing_costs' },
  { id: 'management', name: 'Verwaltung', icon: Home, color: 'green', table: 'management_fees' },
  { id: 'operating', name: 'Betriebskosten', icon: Settings, color: 'purple', table: 'operating_costs' },
  { id: 'legal', name: 'Rechtliche Kosten', icon: Gavel, color: 'red', table: 'legal_costs' },
  { id: 'marketing', name: 'Marketing', icon: Megaphone, color: 'orange', table: 'marketing_costs' }
];

// Document Type Configurations
const documentTypeConfig = {
  invoice: {
    title: 'Rechnung analysieren',
    icon: FileText,
    color: 'blue',
    fields: ['supplier_name', 'invoice_number', 'invoice_date', 'due_date', 'net_amount', 'vat_rate', 'gross_amount', 'category', 'subject'],
    saveTable: 'operating_costs',
    insights: ['supplier', 'duplicate', 'category']
  },
  rental_contract: {
    title: 'Mietvertrag analysieren',
    icon: Home,
    color: 'green',
    fields: ['tenant_name', 'landlord_name', 'property_address', 'rent_start_date', 'rent_end_date', 'monthly_rent', 'deposit', 'contract_type', 'subject'],
    saveTable: 'contracts',
    insights: ['property', 'tenant', 'rent_comparison']
  },
  service_contract: {
    title: 'Dienstleistungsvertrag analysieren',
    icon: Settings,
    color: 'orange',
    fields: ['contractor_name', 'service_type', 'contract_start', 'contract_end', 'service_fee', 'maintenance_interval', 'contract_value', 'subject'],
    saveTable: 'contracts',
    insights: ['contractor', 'service_history', 'cost_analysis']
  },
  handover_protocol: {
    title: 'Übergabeprotokoll analysieren',
    icon: ClipboardCheck,
    color: 'purple',
    fields: ['handover_date', 'property_address', 'tenant_name', 'landlord_name', 'meter_readings', 'defects', 'keys_handed', 'subject'],
    saveTable: 'documents',
    insights: ['property', 'defects', 'meter_comparison']
  },
  utility_bill: {
    title: 'Nebenkostenabrechnung analysieren',
    icon: FileText,
    color: 'red',
    fields: ['billing_period_start', 'billing_period_end', 'property_address', 'tenant_name', 'total_costs', 'heating_costs', 'water_costs', 'subject'],
    saveTable: 'operating_costs',
    insights: ['cost_comparison', 'consumption', 'property']
  },
  contract: {
    title: 'Vertrag analysieren',
    icon: FileCheck,
    color: 'green',
    fields: ['contract_party', 'contract_number', 'start_date', 'end_date', 'contract_value', 'contract_type', 'subject', 'termination_period'],
    saveTable: 'contracts',
    insights: ['party', 'similar', 'expiry']
  },
  general: {
    title: 'Dokument analysieren',
    icon: FileText,
    color: 'purple',
    fields: ['document_name', 'document_date', 'category', 'description', 'related_property'],
    saveTable: 'documents',
    insights: ['similar', 'category']
  }
}



// Extract potential data tokens from OCR text and key_data
const extractUnassignedTokens = (ocrText, keyData, extractedData) => {
  const tokens = [];
  
  // Extract from key_data if available
  if (keyData && typeof keyData === 'object') {
    Object.entries(keyData).forEach(([key, value]) => {
      if (value && typeof value === 'string' && value.trim()) {
        tokens.push({
          id: `key_${key}`,
          value: value.trim(),
          type: key,
          confidence: 0.9,
          source: 'ai_extraction'
        });
      }
    });
  }
  
  // Extract dates (DD.MM.YYYY format)
  const dateRegex = /\b\d{1,2}\.\d{1,2}\.\d{4}\b/g;
  const dates = ocrText.match(dateRegex) || [];
  dates.forEach((date, index) => {
    if (!tokens.some(t => t.value === date)) {
      tokens.push({
        id: `date_${index}`,
        value: date,
        type: 'date',
        confidence: 0.8,
        source: 'text_extraction'
      });
    }
  });
  
  // Extract amounts (€ format)
  const amountRegex = /\b\d+[.,]?\d*\s*€/g;
  const amounts = ocrText.match(amountRegex) || [];
  amounts.forEach((amount, index) => {
    if (!tokens.some(t => t.value === amount)) {
      tokens.push({
        id: `amount_${index}`,
        value: amount,
        type: 'amount',
        confidence: 0.8,
        source: 'text_extraction'
      });
    }
  });
  
  // Extract names (capitalized words)
  const nameRegex = /\b[A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)*\s+(?:GmbH|AG|e\.V\.|KG|OHG)\b|\b[A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)+\b/g;
  const names = ocrText.match(nameRegex) || [];
  names.forEach((name, index) => {
    if (!tokens.some(t => t.value === name) && name.length > 5) {
      tokens.push({
        id: `name_${index}`,
        value: name,
        type: 'name',
        confidence: 0.7,
        source: 'text_extraction'
      });
    }
  });
  
  return tokens.slice(0, 8); // Limit to 8 tokens for UI
};

// Map AI-detected document types to our categories
const mapDocumentTypeToCategory = (aiDocumentType) => {
  const mapping = {
    rental_contract: 'Mietvertrag',
    service_contract: 'Dienstleistung',
    handover_protocol: 'Übergabe',
    utility_bill: 'Nebenkosten',
    invoice: 'Rechnung',
    general: 'Sonstiges'
  };
  return mapping[aiDocumentType] || 'Sonstiges';
};

// Simple Sortable Token Component
// Confidence Badge Component
const ConfidenceBadge = ({ confidence }) => {
  if (!confidence) return null;
  const percentage = Math.round(confidence * 100);
  let color;
  if (confidence >= 0.85) color = 'text-green-600 bg-green-100 dark:bg-green-900/20';
  else if (confidence >= 0.70) color = 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20';
  else color = 'text-red-600 bg-red-100 dark:bg-red-900/20';
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color} ml-2`}>
      {percentage}%
    </span>
  );
};

// Droppable Field Wrapper Component
const DroppableField = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`relative ${isOver ? 'ring-2 ring-blue-500' : ''}`}>
      {children}
    </div>
  );
};

const SortableToken = ({ token }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: token.id,
    data: { value: token.value }
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
         className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm cursor-move hover:bg-blue-200">
      🏷️ {token.value} ({Math.round(token.confidence * 100)}%)
    </div>
  );
};

const UniversalDocumentUpload = ({ isOpen, onClose, onUploadComplete, onBookingSuggestion, documentType = 'invoice' }) => {
  // State Management
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [activeAmountField, setActiveAmountField] = useState(null);
  const [rawOcrText, setRawOcrText] = useState('');
  const [debugLogs, setDebugLogs] = useState([]);
  // Dynamic initial state based on document type
  const getInitialState = (type) => {
    const baseState = {
      document_name: '',
      document_date: '',
      category: '',
      description: '',
      summary: ''
    };

    if (type === 'invoice') {
      return {
        ...baseState,
        supplier_name: '',
        recipient_name: '',
        invoice_number: '',
        invoice_date: '',
        due_date: '',
        service_period_start: '',
        service_period_end: '',
        customer_number: '',
        object_unit: '',
        net_amount: 0,
        vat_rate: 19,
        vat_amount: 0,
        gross_amount: 0,
        amount_options: [],
        subject: '',
        payment_terms: '',
        work_analysis: null,
        co_recipient: '',
        direct_subject: ''
      };
    } else if (type === 'contract') {
      return {
        ...baseState,
        contract_party: '',
        contract_number: '',
        start_date: '',
        end_date: '',
        contract_value: 0,
        contract_type: '',
        termination_period: ''
      };
    } else {
      return {
        ...baseState,
        related_property: '',
        tags: [],
        file_type: ''
      };
    }
  };

  const [extractedData, setExtractedData] = useState(getInitialState(documentType));

  // Supabase integration state
  const [supplierInsights, setSupplierInsights] = useState(null);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [canDirectSave, setCanDirectSave] = useState(false);

  // AI Model selection
  const [selectedModel, setSelectedModel] = useState('qwen'); // qwen (default), gemini, gpt4
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [fieldConfidence, setFieldConfidence] = useState({});
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  // Drag & Drop Beta state
  const [unassignedTokens, setUnassignedTokens] = useState([]);
  const [draggedToken, setDraggedToken] = useState(null);

  // @dnd-kit sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const draggedValue = active.data.current?.value;
    const fieldName = over.id;
    if (draggedValue && fieldName) {
      setExtractedData(prev => ({ ...prev, [fieldName]: draggedValue }));
      setUnassignedTokens(prev => prev.filter(t => t.id !== active.id));
    }
  };
  const [dropTarget, setDropTarget] = useState(null);

  const fileInputRef = useRef(null);


  
  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e, fieldName) => {
    e.preventDefault();
    const droppedValue = e.dataTransfer.getData('text/plain');
    
    if (draggedToken && droppedValue) {
      // Update the field with dropped value
      setExtractedData(prev => ({
        ...prev,
        [fieldName]: droppedValue
      }));
      
      // Remove token from unassigned list
      setUnassignedTokens(prev => prev.filter(t => t.id !== draggedToken.id));
      
      // Reset drag state
      setDraggedToken(null);
      setDropTarget(null);
      
      // Success feedback
      console.log(`✅ Dropped "${droppedValue}" into ${fieldName}`);
    }
  };

  const handleDragEnter = (e, fieldName) => {
    e.preventDefault();
    setDropTarget(fieldName);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDropTarget(null);
  };

  const resetModalState = useCallback(() => {
    console.log('🔄 Resetting modal state...');
    setFile(null);
    setUploading(false);
    setAnalyzing(false);
    setShowResults(false);
    setActiveAmountField(null);
    setRawOcrText('');
    setDebugLogs([]);
    setExtractedData({
      supplier_name: '',
      recipient_name: '',
      invoice_number: '',
      invoice_date: '',
      due_date: '',
      service_period_start: '',
      service_period_end: '',
      customer_number: '',
      object_unit: '',
      category: '',
      net_amount: 0,
      vat_rate: 19,
      vat_amount: 0,
      gross_amount: 0,
      amount_options: [],
      subject: '',
      payment_terms: '',
      summary: '',
      work_analysis: null,
      co_recipient: '',
      direct_subject: ''
    });
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    console.log('✅ Modal state reset complete');
  }, []);

  // ✅ BUG FIX: Reset modal when closed - MUST be before early return
  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
  }, [isOpen, resetModalState]);

  // Early return check - after all hooks



  // Early return check - after all hooks
  if (!isOpen) return null;

  // OCR Analysis
  const analyzeDocument = async () => {

    setUploading(true);
    setAnalyzing(true);
    addDebugLog('INFO', 'OCR-Analyse gestartet...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('model', selectedModel);

      const response = await fetch('/api/ocr/analyze', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        addDebugLog('SUCCESS', 'OCR-Analyse erfolgreich abgeschlossen');
        setRawOcrText(result.data.extracted_text || '');
        
        const ocr = result.data;
        
        // Smart Field Mapper Integration
        try {
          const sourceFields = Object.keys(ocr);
          const mapperResponse = await fetch('https://clara360.de/api/field-mapper/map', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sourceFields: sourceFields,
              targetTable: documentType === 'invoice' ? 'invoice' : 'document',
              language: 'de'
            })
          });
          
          const mappingResult = await mapperResponse.json();
          addDebugLog('SUCCESS', `Field Mapper: ${mappingResult.mappings?.length || 0} Felder gemappt`);
          
          // Auto-fill high-confidence mappings
          const mappedData = {};
          const lowConfidenceTokens = [];
          
          const confidenceScores = {};
          mappingResult.mappings?.forEach(mapping => {
            if (mapping.confidence >= 0.85) {
              // High confidence - auto-fill
              mappedData[mapping.targetField] = ocr[mapping.sourceField];
              confidenceScores[mapping.targetField] = mapping.confidence;
            } else if (mapping.confidence >= 0.50) {
              // Low confidence - add to token box
              lowConfidenceTokens.push({
                id: `token_${mapping.sourceField}`,
                value: ocr[mapping.sourceField],
                type: mapping.sourceField,
                confidence: mapping.confidence,
                source: 'smart_mapper'
              });
            }
          });
          setFieldConfidence(confidenceScores);
          
          // Fallback to direct mapping for unmapped fields
          mappedData.supplier_name = mappedData.supplier_name || ocr.supplier_name || '';
          mappedData.invoice_number = mappedData.invoice_number || ocr.invoice_number || '';
          mappedData.invoice_date = mappedData.invoice_date || ocr.invoice_date || '';
          mappedData.net_amount = parseFloat(mappedData.net_amount || ocr.amount_net) || 0;
          mappedData.gross_amount = parseFloat(mappedData.gross_amount || ocr.amount_gross) || 0;
          mappedData.subject = mappedData.subject || ocr.summary || 'Automatisch erkanntes Dokument';
          
          setExtractedData(mappedData);
          setUnassignedTokens(lowConfidenceTokens);
          addDebugLog('INFO', `${lowConfidenceTokens.length} unsichere Felder in Token-Box`);
        } catch (mapperError) {
          addDebugLog('WARN', 'Field Mapper Fehler, verwende Fallback-Mapping');
          // Fallback to simple mapping
          const mappedData = {
            supplier_name: ocr.supplier_name || '',
            invoice_number: ocr.invoice_number || '',
            invoice_date: ocr.invoice_date || '',
            net_amount: parseFloat(ocr.amount_net) || 0,
            gross_amount: parseFloat(ocr.amount_gross) || 0,
            subject: ocr.summary || 'Automatisch erkanntes Dokument'
          };
          setExtractedData(mappedData);
        }
        
        setOcrResult(result);
        setShowResults(true);
      } else {
        addDebugLog('ERROR', 'Fehler bei OCR-Analyse: ' + (result.error || 'Unbekannter Fehler'));
      }
    } catch (error) {
      addDebugLog('ERROR', 'Fehler bei OCR-Analyse: ' + error.message);
    } finally {
      setAnalyzing(false);
      setUploading(false);
    }
  };
  // German date parsing function
  const parseDeutschesDatum = (dateStr) => {
    if (!dateStr) return '';
    
    // Handle German date formats like "30. Dezember 2024" or "30.12.2024"
    const germanMonths = {
      'januar': '01', 'februar': '02', 'märz': '03', 'april': '04',
      'mai': '05', 'juni': '06', 'juli': '07', 'august': '08',
      'september': '09', 'oktober': '10', 'november': '11', 'dezember': '12'
    };
    
    // Try German month names first
    const monthNameMatch = dateStr.toLowerCase().match(/(\d{1,2})\.\s*(\w+)\s*(\d{4})/);
    if (monthNameMatch) {
      const [, day, monthName, year] = monthNameMatch;
      const month = germanMonths[monthName.toLowerCase()];
      if (month) {
        return `${year}-${month}-${day.padStart(2, '0')}`;
      }
    }
    
    // Try DD.MM.YYYY format
    const numericMatch = dateStr.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (numericMatch) {
      const [, day, month, year] = numericMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Return original if no pattern matches
    return dateStr;
  };

  // 🚀 SUPABASE INTEGRATION FUNCTIONS
  
  // Supplier Recognition
  const recognizeSupplier = async (supplierName) => {
    if (!supplierName) return null;
    
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name, type, email, phone, address')
        .ilike('name', `%${supplierName}%`)
        .limit(5);
      
      if (error) {
        console.error('Supplier recognition error:', error);
        return null;
      }
      
      return data && data.length > 0 ? data[0] : null;
    } catch (err) {
      console.error('Supplier recognition failed:', err);
      return null;
    }
  };

  // Duplicate Check
  const checkDuplicate = async (supplierName, invoiceNumber) => {
    if (!supplierName || !invoiceNumber) return { exact: false, similar: false };
    
    try {
      const { data, error } = await supabase
        .from('operating_costs')
        .select('id, cost_date, gross_amount')
        .eq('supplier_name', supplierName)
        .eq('invoice_number', invoiceNumber)
        .limit(1);
      
      if (error) {
        console.error('Duplicate check error:', error);
        return { exact: false, similar: false };
      }
      
      return { 
        exact: data && data.length > 0, 
        similar: false,
        existingRecord: data && data.length > 0 ? data[0] : null
      };
    } catch (err) {
      console.error('Duplicate check failed:', err);
      return { exact: false, similar: false };
    }
  };

  // Supplier Insights
  const getSupplierInsights = async (supplierName) => {
    if (!supplierName) return null;
    
    try {
      const { data, error } = await supabase
        .from('operating_costs')
        .select('gross_amount, cost_date')
        .eq('supplier_name', supplierName)
        .order('cost_date', { ascending: false })
        .limit(12);
      
      if (error) {
        console.error('Supplier insights error:', error);
        return null;
      }
      
      if (!data || data.length === 0) {
        return { status: 'new', invoiceCount: 0 };
      }
      
      const amounts = data.map(d => d.gross_amount || 0);
      const avgAmount = amounts.reduce((sum, amt) => sum + amt, 0) / amounts.length;
      
      return {
        status: 'known',
        invoiceCount: data.length,
        avgAmount: Math.round(avgAmount * 100) / 100,
        lastInvoice: data[0].cost_date,
        amounts: amounts
      };
    } catch (err) {
      console.error('Supplier insights failed:', err);
      return null;
    }
  };

  // Direct Save to Database
  const saveDirectly = async (ocrData) => {
    try {
      // Check for duplicates first
      const dupeCheck = await checkDuplicate(ocrData.supplier_name, ocrData.invoice_number);
      if (dupeCheck.exact) {
        throw new Error('Duplikat erkannt - Rechnung bereits vorhanden');
      }
      
      const { data, error } = await supabase
        .from('operating_costs')
        .insert({
          supplier_name: ocrData.supplier_name,
          invoice_number: ocrData.invoice_number,
          invoice_date: parseDeutschesDatum(ocrData.invoice_date),
          gross_amount: ocrData.gross_amount,
          net_amount: ocrData.net_amount,
          vat_amount: ocrData.vat_amount,
          vat_rate: ocrData.vat_rate,
          cost_type: ocrData.category || 'Betriebskosten',
          description: ocrData?.subject || ocrData?.summary || "Automatisch erkanntes Dokument",
          due_date: parseDeutschesDatum(ocrData.due_date),
          cost_date: parseDeutschesDatum(ocrData.invoice_date),
          created_at: new Date().toISOString()
        });
      
      if (error) {
        throw error;
      }
      
      addDebugLog('success', '✅ Rechnung erfolgreich gespeichert!');
      return { success: true, data };
    } catch (err) {
      addDebugLog('error', `❌ Speichern fehlgeschlagen: ${err.message}`);
      throw err;
    }
  };

  // 🚀 AUTO-ANALYSIS: Analyze supplier data after OCR
  const analyzeSupplierData = async (supplierName, invoiceNumber, amount) => {
    try {
      addDebugLog('info', '🔍 Analysiere Lieferantendaten...');
      
      // Run all analyses in parallel
      const [supplier, duplicate, insights] = await Promise.all([
        recognizeSupplier(supplierName),
        checkDuplicate(supplierName, invoiceNumber),
        getSupplierInsights(supplierName)
      ]);
      
      setSupplierInsights(insights);
      setDuplicateCheck(duplicate);
      
      // Determine if direct save is possible
      const confidence = 0.85; // From OCR
      const canSave = confidence >= 0.8 && !duplicate.exact && supplier;
      setCanDirectSave(canSave);
      
      // Log results
      if (supplier) {
        addDebugLog('success', `✅ Lieferant erkannt: ${supplier.name} (${supplier.type})`);
      } else {
        addDebugLog('info', '🆕 Neuer Lieferant erkannt');
      }
      
      if (duplicate.exact) {
        addDebugLog('warning', '⚠️ Duplikat erkannt - Rechnung bereits vorhanden');
      }
      
      if (insights && insights.status === 'known') {
        addDebugLog('info', `📊 ${insights.invoiceCount} Rechnungen, Ø €${insights.avgAmount}`);
      }
      
    } catch (error) {
      addDebugLog('error', `❌ Analyse fehlgeschlagen: ${error.message}`);
    }
  };

  // Reset Function
  const calculateAmounts = (field, value) => {
    const numValue = parseFloat(value) || 0;
    const vatRate = extractedData.vat_rate / 100;

    let newData = { ...extractedData };

    if (field === 'net_amount') {
      newData.net_amount = numValue;
      newData.vat_amount = numValue * vatRate;
      newData.gross_amount = numValue + newData.vat_amount;
    } else if (field === 'gross_amount') {
      newData.gross_amount = numValue;
      newData.net_amount = numValue / (1 + vatRate);
      newData.vat_amount = numValue - newData.net_amount;
    } else if (field === 'vat_rate') {
      const newVatRate = numValue / 100;
      if (newData.net_amount > 0) {
        newData.vat_rate = numValue;
        newData.vat_amount = newData.net_amount * newVatRate;
        newData.gross_amount = newData.net_amount + newData.vat_amount;
      } else if (newData.gross_amount > 0) {
        newData.vat_rate = numValue;
        newData.net_amount = newData.gross_amount / (1 + newVatRate);
      }
    setExtractedData(newData);
  };
  // Document handling function
  const handleDocumentData = () => {
    return {
        filename: file?.name || 'unknown.jpg',
        file_url: uploadedFileUrl,
        ai_parsed_data: {
          document_type: extractedData.document_type,
          supplier_name: extractedData.supplier_name,
          recipient_name: extractedData.recipient_name,
          invoice_number: extractedData.invoice_number,
          amount: parseFloat(extractedData.gross_amount) || 0,
          date: extractedData.invoice_date,
          summary: extractedData.summary,
          key_data: extractedData
        }
      }
    };

    // Call the correct callback function
    if (onUploadComplete) {
      onUploadComplete(result);
    }
    
    // Reset modal state before closing
    resetModalState();
    onClose();
  };

  // Handle data acceptance - calls handleDocumentData to process and submit
  const handleDataAccept = () => {
    console.log("📋 Accepting extracted data...");
    handleDocumentData();
  };

  // Dynamic layout classes based on content availability
  const hasOcrContent = rawOcrText || debugLogs.length > 0;
  const hasInsights = showResults && (extractedData.summary || extractedData.document_type || extractedData.supplier_name);
  
  let layoutClasses = "flex h-full";
  let leftPanelClasses = "";
  let centerPanelClasses = "";
  let rightPanelClasses = "";

  if (!hasOcrContent && !hasInsights) {
    // Phase 1: Only upload
    centerPanelClasses = "w-full";
  } else if (hasOcrContent && !hasInsights) {
    // Phase 2: Upload + OCR
    leftPanelClasses = "w-1/3 border-r border-gray-200 dark:border-gray-700";
    centerPanelClasses = "w-2/3";
  } else if (hasOcrContent && hasInsights) {
    // Phase 3: All content
    leftPanelClasses = "w-1/4 border-r border-gray-200 dark:border-gray-700";
    centerPanelClasses = "w-1/2 border-r border-gray-200 dark:border-gray-700";
    rightPanelClasses = "w-1/4";
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {documentTypeConfig[documentType]?.title || 'Dokument analysieren'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                OCR bereit • Intelligente Datenextraktion
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* AI Model Selector */}
            <div className="relative">
              <button
                onClick={() => setShowModelSelector(!showModelSelector)}
                className="px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700"
              >
                <Brain className="w-4 h-4" />
                <span>
                  {selectedModel === 'qwen' && '⚡ Clara'}
                  {selectedModel === 'gemini' && '✨ Gemini'}
                  {selectedModel === 'gpt4' && '🧠 GPT-4'}
                </span>
              </button>
              
              {/* Model Selector Dropdown */}
              {showModelSelector && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50">
                  <div className="p-2">
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">
                      KI-Modell wählen
                    </div>
                    
                    {/* Mistral Option */}
                    <button
                      onClick={() => {
                        setSelectedModel('qwen');
                        setShowModelSelector(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        selectedModel === 'qwen'
                          ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Zap className="w-5 h-5 text-purple-500" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-white">Qwen</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Sehr Schnell Schnell & Kostenlos • Lokal Kostenlos • Lokal</div>
                        </div>
                        {selectedModel === 'qwen' && (
                          <CheckCircle className="w-4 h-4 text-purple-500" />
                        )}
                      </div>
                    </button>
                    
                    {/* Gemini Option */}
                    <button
                      onClick={() => {
                        setSelectedModel('gemini');
                        setShowModelSelector(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors mt-1 ${
                        selectedModel === 'gemini'
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Sparkles className="w-5 h-5 text-blue-500" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-white">Gemini</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Beste OCR-Qualität • Google</div>
                        </div>
                        {selectedModel === 'gemini' && (
                          <CheckCircle className="w-4 h-4 text-blue-500" />
                        )}
                      </div>
                    </button>
                    
                    {/* GPT-4 Option */}
                    <button
                      onClick={() => {
                        setSelectedModel('gpt4');
                        setShowModelSelector(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors mt-1 ${
                        selectedModel === 'gpt4'
                          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Brain className="w-5 h-5 text-green-500" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-white">GPT-4</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Fallback • OpenAI</div>
                        </div>
                        {selectedModel === 'gpt4' && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {(hasOcrContent || showResults) && (
              <button
                onClick={() => setDebugMode(!debugMode)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  debugMode 
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' 
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                }`}
              >
                Debug
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Dynamic Content Layout */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className={`${layoutClasses} flex-1 overflow-hidden`}>
          
          {/* Left Panel - OCR Text & Debug */}
          {hasOcrContent && (
            <div className={`${leftPanelClasses} flex flex-col overflow-hidden`}>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Extrahierter Text
                </h3>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Drag & Drop Beta - Unassigned Tokens */}
          {unassignedTokens.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">🧪 Nicht zugeordnete Daten (Beta)</span>
                </div>
                <SortableContext items={unassignedTokens.map(t => t.id)} strategy={verticalListSortingStrategy}>
                  <div className="flex flex-wrap gap-2">
                    {unassignedTokens.slice(0, 6).map(token => (
                      <SortableToken key={token.id} token={token} />
                    ))}
                  </div>
                </SortableContext>
                <p className="text-xs text-gray-500 mt-2">Ziehen Sie Daten auf die Eingabefelder</p>
              </div>
          )}
                {/* OCR Text */}
                {rawOcrText && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      Konfidenz: 100% • {rawOcrText.length} Zeichen extrahiert
                    </div>
                    <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-3 max-h-96 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
                        {rawOcrText}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Debug Logs */}
                {debugMode && debugLogs.length > 0 && (
                  <div className="bg-black rounded-lg p-3">
                    <div className="text-xs text-green-400 mb-2 flex items-center">
                      <Terminal className="w-3 h-3 mr-1" />
                      Debug-Logs
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                      {debugLogs.map((log, index) => (
                        <div key={index} className="text-xs mb-1">
                          <span className="text-gray-500">[{log.timestamp}]</span>
                          <span className={`ml-2 ${
                            log.type === 'ERROR' ? 'text-red-400' : 
                            log.type === 'SUCCESS' ? 'text-green-400' : 
                            'text-yellow-400'
                          }`}>
                            {log.type}
                          </span>
                          <span className="ml-2 text-green-400">{log.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Center Panel - Main Content */}
          <div className={`${centerPanelClasses} flex flex-col overflow-hidden`}>
            <div className="flex-1 overflow-y-auto p-6">
              
              {/* Upload Section */}
              {!showResults && (
                <div className="space-y-6">
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Dokument hochladen
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      Verträge, Rechnungen, Übergabeprotokolle & mehr - Ziehen Sie eine Datei hierher oder klicken Sie zum Auswählen
                    </p>
                    <div className="text-sm text-gray-400 dark:text-gray-500">
                      Unterstützt: PDF, JPG, PNG, WebP, DOCX • Max. 10MB
                    </div>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.doc"
                    onChange={(e) => { const file = e.target.files?.[0]; if (file && file.size <= 10 * 1024 * 1024) { setFile(file); setShowResults(false); setOcrResult(null); setExtractedData(null); setAnalyzing(false); setAnalysisProgress(0); setShowReviewModal(false); console.log("✅ Datei erfolgreich ausgewählt:", file.name); } else if (file) { console.error("Datei zu groß (max. 10MB)"); } }}
                    className="hidden"
                  />

                  {file && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={analyzeDocument}
                          disabled={uploading || analyzing}
                          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors flex items-center space-x-2"
                        >
                          {analyzing ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              <span>Analysiere...</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              <span>OCR starten</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Results Section */}
              {showResults && (
                <div className="space-y-6">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                      <div>
                        <div className="font-medium text-green-900 dark:text-green-100">
                          Datenextraktion erfolgreich
                        </div>
                        <div className="text-sm text-green-700 dark:text-green-300">
                          Konfidenz: 100% • {rawOcrText.length} Zeichen extrahiert
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Fields */}
                  {documentType === 'invoice' ? (
                    /* INVOICE FORM - Original, unverändert */
                    <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    {/* Rechnungssteller */}
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Building className="w-4 h-4 mr-2" />
                        Rechnungssteller (Absender)
                        <ConfidenceBadge confidence={fieldConfidence.supplier_name} />
                      </label>
                      <DroppableField id="supplier_name">
                      <input
                        type="text"
                        value={extractedData.supplier_name}
                        onChange={(e) => setExtractedData(prev => ({...prev, supplier_name: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      </DroppableField>
                    </div>

                    {/* Rechnungsempfänger */}
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <User className="w-4 h-4 mr-2" />
                        Rechnungsempfänger (Adressat)
                      </label>
                      <input
                        type="text"
                        value={extractedData.recipient_name}
                        onChange={(e) => setExtractedData(prev => ({...prev, recipient_name: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Rechnungsnummer */}
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Hash className="w-4 h-4 mr-2" />
                        Rechnungsnummer
                        <ConfidenceBadge confidence={fieldConfidence.invoice_number} />
                      </label>
                      <DroppableField id="invoice_number">
                      <input
                        type="text"
                        value={extractedData.invoice_number}
                        onChange={(e) => setExtractedData(prev => ({...prev, invoice_number: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      </DroppableField>
                    </div>

                    {/* Rechnungsdatum */}
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Calendar className="w-4 h-4 mr-2" />
                        Rechnungsdatum
                        <ConfidenceBadge confidence={fieldConfidence.invoice_date} />
                      </label>
                      <DroppableField id="invoice_date">
                      <input
                        type="date"
                        value={extractedData.invoice_date}
                        onChange={(e) => setExtractedData(prev => ({...prev, invoice_date: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      </DroppableField>
                    </div>

                    {/* Kategorie */}
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <FileCheck className="w-4 h-4 mr-2" />
                        Kategorie
                      </label>
                      <DroppableField id="category">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {costCategories.map((category) => {
                          const Icon = category.icon;
                          const isSelected = extractedData.category === category.id;
                          return (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => {
                                setExtractedData(prev => ({
                                  ...prev,
                                  category: category.id
                                }));
                              }}
                              className={`p-3 border rounded-lg text-left transition-colors ${
                                isSelected
                                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                              }`}
                            >
                              <Icon className="w-5 h-5 mb-2" />
                              <div className="text-sm font-medium">{category.name}</div>
                            </button>
                          );
                        })}
                      </div>
                      </DroppableField>
                    </div>

                    {/* Objekt/Unit */}
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Building className="w-4 h-4 mr-2" />
                        Objekt/Unit
                      </label>
                      <input
                        type="text"
                        value={extractedData.object_unit || ''}
                        onChange={(e) => setExtractedData(prev => ({...prev, object_unit: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="P7, Einheit 10, 4. OG"
                      />
                    </div>
                  </div>

                  {/* Beträge & Steuern Section */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <Euro className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Beträge & Steuern</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Euro className="w-4 h-4 mr-2" />
                          Netto-Betrag
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={extractedData.net_amount}
                          onChange={(e) => calculateAmounts('net_amount', e.target.value)}
                          onFocus={() => setActiveAmountField('net_amount')}
                          className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            activeAmountField === 'net_amount' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                      </div>

                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Percent className="w-4 h-4 mr-2" />
                          MwSt-Satz (%)
                        </label>
                        <DroppableField id="vat_rate">
                        <select
                          value={extractedData.vat_rate}
                          onChange={(e) => calculateAmounts('vat_rate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value={0}>0% (steuerfrei)</option>
                          <option value={7}>7% (ermäßigt)</option>
                          <option value={19}>19% (regulär)</option>
                        </select>
                        </DroppableField>
                      </div>

                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <TrendingUp className="w-4 h-4 mr-2" />
                          MwSt-Betrag
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={extractedData.vat_amount}
                          onChange={(e) => setExtractedData(prev => ({...prev, vat_amount: parseFloat(e.target.value) || 0}))}
                          onFocus={() => setActiveAmountField('vat_amount')}
                          className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            activeAmountField === 'vat_amount' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                      </div>

                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Euro className="w-4 h-4 mr-2" />
                          Gesamtbetrag
                          <ConfidenceBadge confidence={fieldConfidence.gross_amount} />
                        </label>
                        <DroppableField id="gross_amount">
                        <input
                          type="number"
                          step="0.01"
                          value={extractedData.gross_amount}
                          onChange={(e) => calculateAmounts('gross_amount', e.target.value)}
                          onFocus={() => setActiveAmountField('gross_amount')}
                          className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent font-semibold ${
                            activeAmountField === 'gross_amount' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                        </DroppableField>
                      </div>
                    </div>

                    {/* Erkannte Beträge */}
                    {extractedData.amount_options && extractedData.amount_options.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                          Erkannte Beträge zur Auswahl:
                        </label>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {extractedData.amount_options.map((option, index) => (
                            <button
                              key={index}
                              onClick={() => handleAmountClick(option.value)}
                              className="w-full text-left px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg text-sm transition-colors"
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                        {activeAmountField && (
                          <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                            💡 Klicken Sie auf einen Betrag, um ihn in das aktive Feld ({activeAmountField === 'net_amount' ? 'Netto' : activeAmountField === 'gross_amount' ? 'Brutto' : 'MwSt'}) zu übernehmen
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Additional Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    {/* Betreff/Verwendungszweck */}
                    <div className="md:col-span-2">
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <FileCheck className="w-4 h-4 mr-2" />
                        Betreff/Verwendungszweck
                      </label>
                      <input
                        type="text"
                        value={extractedData.subject}
                        onChange={(e) => setExtractedData(prev => ({...prev, subject: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Beschreibung der Leistung"
                      />
                    </div>

                    {/* Fälligkeitsdatum */}
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Clock className="w-4 h-4 mr-2" />
                        Fälligkeitsdatum
                      </label>
                      <input
                        type="date"
                        value={extractedData.due_date}
                        onChange={(e) => setExtractedData(prev => ({...prev, due_date: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Kundennummer */}
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Hash className="w-4 h-4 mr-2" />
                        Kundennummer
                      </label>
                      <input
                        type="text"
                        value={extractedData.customer_number}
                        onChange={(e) => setExtractedData(prev => ({...prev, customer_number: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="KD-12345"
                      />
                    </div>

                    {/* Leistungszeitraum */}
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Calendar className="w-4 h-4 mr-2" />
                        Leistung von
                      </label>
                      <input
                        type="date"
                        value={extractedData.service_period_start}
                        onChange={(e) => setExtractedData(prev => ({...prev, service_period_start: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Calendar className="w-4 h-4 mr-2" />
                        Leistung bis
                      </label>
                      <input
                        type="date"
                        value={extractedData.service_period_end}
                        onChange={(e) => setExtractedData(prev => ({...prev, service_period_end: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Zahlungsbedingungen */}
                    <div className="md:col-span-2">
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Clock className="w-4 h-4 mr-2" />
                        Zahlungsbedingungen
                      </label>
                      <input
                        type="text"
                        value={extractedData.payment_terms}
                        onChange={(e) => setExtractedData(prev => ({...prev, payment_terms: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="z.B. 14 Tage netto"
                      />
                    </div>

                    {/* Zusammenfassung */}
                    <div className="md:col-span-2">
                      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <FileText className="w-4 h-4 mr-2" />
                        Zusammenfassung
                      </label>
                      <textarea
                        value={extractedData.summary}
                        onChange={(e) => setExtractedData(prev => ({...prev, summary: e.target.value}))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Automatisch generierte Zusammenfassung der Rechnung..."
                      />
                    </div>
                  </div>
                  </>
                  ) : (
                    /* CONTRACT / GENERAL FORM - Dynamic */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {documentType === 'contract' && (
                        <>
                          {/* Contract Party */}
                          <div className="md:col-span-2">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <User className="w-4 h-4 mr-2" />
                              Vertragspartei <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                              type="text"
                              value={extractedData.contract_party || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, contract_party: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          {/* Contract Number */}
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <Hash className="w-4 h-4 mr-2" />
                              Vertragsnummer
                            </label>
                            <input
                              type="text"
                              value={extractedData.contract_number || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, contract_number: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          {/* Contract Type */}
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <FileText className="w-4 h-4 mr-2" />
                              Vertragsart <span className="text-red-500 ml-1">*</span>
                            </label>
                            <select
                              value={extractedData.contract_type || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, contract_type: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Bitte wählen...</option>
                              <option value="Mietvertrag">Mietvertrag</option>
                              <option value="Dienstleistungsvertrag">Dienstleistungsvertrag</option>
                              <option value="Kaufvertrag">Kaufvertrag</option>
                              <option value="Wartungsvertrag">Wartungsvertrag</option>
                              <option value="Sonstiges">Sonstiges</option>
                            </select>
                          </div>

                          {/* Start Date */}
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <Calendar className="w-4 h-4 mr-2" />
                              Vertragsbeginn <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                              type="date"
                              value={extractedData.start_date || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, start_date: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          {/* End Date */}
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <Calendar className="w-4 h-4 mr-2" />
                              Vertragsende
                            </label>
                            <input
                              type="date"
                              value={extractedData.end_date || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, end_date: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          {/* Contract Value */}
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <Euro className="w-4 h-4 mr-2" />
                              Vertragswert (€)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={extractedData.contract_value || 0}
                              onChange={(e) => setExtractedData(prev => ({...prev, contract_value: parseFloat(e.target.value) || 0}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          {/* Termination Period */}
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <Clock className="w-4 h-4 mr-2" />
                              Kündigungsfrist
                            </label>
                            <input
                              type="text"
                              value={extractedData.termination_period || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, termination_period: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="z.B. 3 Monate"
                            />
                          </div>

                          {/* Subject */}
                          <div className="md:col-span-2">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <FileText className="w-4 h-4 mr-2" />
                              Vertragsgegenstand
                            </label>
                            <textarea
                              value={extractedData.subject || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, subject: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              rows={3}
                            />
                          </div>
                        </>
                      )}

                      {documentType === 'general' && (
                        <>
                          {/* Document Name */}
                          <div className="md:col-span-2">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <FileText className="w-4 h-4 mr-2" />
                              Dokumentname <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                              type="text"
                              value={extractedData.document_name || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, document_name: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          {/* Document Date */}
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <Calendar className="w-4 h-4 mr-2" />
                              Dokumentdatum
                            </label>
                            <input
                              type="date"
                              value={extractedData.document_date || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, document_date: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          {/* Category */}
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <FileText className="w-4 h-4 mr-2" />
                              Kategorie
                            </label>
                            <select
                              value={extractedData.category || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, category: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Bitte wählen...</option>
                              <option value="Korrespondenz">Korrespondenz</option>
                              <option value="Protokoll">Protokoll</option>
                              <option value="Bericht">Bericht</option>
                              <option value="Sonstiges">Sonstiges</option>
                            </select>
                          </div>

                          {/* Related Property */}
                          <div className="md:col-span-2">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <Building className="w-4 h-4 mr-2" />
                              Zugehörige Immobilie
                            </label>
                            <input
                              type="text"
                              value={extractedData.related_property || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, related_property: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          {/* Description */}
                          <div className="md:col-span-2">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              <FileText className="w-4 h-4 mr-2" />
                              Beschreibung
                            </label>
                            <textarea
                              value={extractedData.description || ''}
                              onChange={(e) => setExtractedData(prev => ({...prev, description: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              rows={3}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            {showResults && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800">
                <div className="flex justify-between items-center">
                  {/* Left side - Direct Save Button */}
                  {canDirectSave && !duplicateCheck?.exact && (
                    <button
                      onClick={async () => {
                        try {
                          await saveDirectly(extractedData);
                          onClose();
                        } catch (error) {
                          // Error already logged in saveDirectly
                        }
                      }}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Direkt speichern</span>
                    </button>
                  )}
                  
                  {duplicateCheck?.exact && (
                    <div className="px-4 py-2 bg-red-100 text-red-800 rounded-lg text-sm">
                      ⚠️ Duplikat erkannt
                    </div>
                  )}
                  
                  {/* Right side - Standard Buttons */}
                  <div className="flex space-x-3">
                    <button
                      onClick={onClose}
                      className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={handleDataAccept}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Daten übernehmen</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Clara Insights */}
          {hasInsights && (
            <div className={`${rightPanelClasses} flex flex-col overflow-hidden`}>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded flex items-center justify-center">
                    <span className="text-white font-bold text-xs">AI</span>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Clara Insights</h3>
                  <div className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                    KI
                  </div>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                
                {/* Preis-Insights */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">Preis-Analyse</h4>
                  </div>
                  <div className="space-y-2">
                    {extractedData.work_analysis && (
                      <div className="flex items-center space-x-2 text-xs">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-700 dark:text-gray-300">
                          {extractedData.work_analysis.hourly_rate}€/h - 
                          <span className="text-green-600 dark:text-green-400 ml-1">Marktüblich</span>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-xs">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-700 dark:text-gray-300">
                        {extractedData.category}
                      </span>
                    </div>
                    {extractedData.gross_amount > 500 && (
                      <div className="flex items-center space-x-2 text-xs">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <span className="text-gray-700 dark:text-gray-300">
                          Größere Ausgabe - <span className="text-yellow-600 dark:text-yellow-400">Prüfung empfohlen</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lieferanten-Insights */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">Lieferant</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-xs">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-700 dark:text-gray-300 truncate">
                        {extractedData.supplier_name}
                      </span>
                    </div>
                    
                    {/* Dynamic Supplier Insights */}
                    {supplierInsights ? (
                      supplierInsights.status === 'known' ? (
                        <>
                          <div className="flex items-center space-x-2 text-xs">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-gray-700 dark:text-gray-300">
                              <span className="text-green-600 dark:text-green-400">Bekannter Lieferant</span> - {supplierInsights.invoiceCount} Rechnungen
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-xs">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span className="text-gray-700 dark:text-gray-300">
                              Ø €{supplierInsights.avgAmount} - Letzte: {new Date(supplierInsights.lastInvoice).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center space-x-2 text-xs">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span className="text-gray-700 dark:text-gray-300">
                            <span className="text-yellow-600 dark:text-yellow-400">Neuer Lieferant</span> - Erste Rechnung
                          </span>
                        </div>
                      )
                    ) : (
                      <div className="flex items-center space-x-2 text-xs">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                        <span className="text-gray-500 dark:text-gray-400">
                          Analysiere Lieferant...
                        </span>
                      </div>
                    )}
                    
                    {/* Duplicate Warning */}
                    {duplicateCheck?.exact && (
                      <div className="flex items-center space-x-2 text-xs">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-red-600 dark:text-red-400">
                          ⚠️ Duplikat erkannt
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Objekt-Insights */}
                {extractedData.object_unit && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <Building className="w-4 h-4 text-indigo-500" />
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Objekt</h4>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-xs">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                        <span className="text-gray-700 dark:text-gray-300 truncate">
                          {extractedData.object_unit}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <span className="text-gray-700 dark:text-gray-300">
                          Historie wird aufgebaut
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Empfehlungen */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">Empfehlungen</h4>
                  </div>
                  <div className="space-y-2">
                    {extractedData.work_analysis && extractedData.work_analysis.hours > 3 && (
                      <div className="flex items-center space-x-2 text-xs">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <span className="text-gray-700 dark:text-gray-300">
                          <span className="text-yellow-600 dark:text-yellow-400">Wartungsvertrag prüfen</span>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-xs">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-700 dark:text-gray-300">
                        Dokumentation vollständig
                      </span>
                    </div>
                    {extractedData.category === 'Reparatur Elektrik' && (
                      <div className="flex items-center space-x-2 text-xs">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-700 dark:text-gray-300">
                          Elektrik-Check empfohlen
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        </DndContext>
      </div>
    </div>
  );
};

export default UniversalDocumentUpload;

